/**
 * Admin Users page - manage users across tenants
 */
import { useState, useMemo } from 'react';
import { useAdminUsers, useUpdateAdminUser, useResetPassword } from '../hooks/useAdminUsers';
import { useTenants } from '../hooks';
import { Button, LoadingCard } from '../components/common';
import type { AdminUser } from '../types/admin-users';
import type { Tenant } from '../types';

export function AdminUsersPage() {
  const { data: tenants, isLoading: tenantsLoading } = useTenants();
  const [selectedTenantId, setSelectedTenantId] = useState<string>('');
  const { data: users, isLoading: usersLoading } = useAdminUsers(selectedTenantId || undefined);
  const updateUser = useUpdateAdminUser();
  const resetPassword = useResetPassword();

  const [actionInProgress, setActionInProgress] = useState<string | null>(null);

  // Map tenant IDs to names for display
  const tenantMap = useMemo(() => {
    const map: Record<string, Tenant> = {};
    tenants?.forEach((t) => {
      map[t.id] = t;
    });
    return map;
  }, [tenants]);

  // Stats
  const stats = useMemo(() => {
    if (!users) return { total: 0, enabled: 0, disabled: 0 };
    return {
      total: users.length,
      enabled: users.filter((u) => u.enabled).length,
      disabled: users.filter((u) => !u.enabled).length,
    };
  }, [users]);

  const handleToggleEnabled = async (user: AdminUser) => {
    const action = user.enabled ? 'disable' : 'enable';
    if (!confirm(`Are you sure you want to ${action} ${user.email || user.id}?`)) {
      return;
    }

    setActionInProgress(user.id);
    try {
      await updateUser.mutateAsync({
        userId: user.id,
        data: { enabled: !user.enabled },
      });
    } catch (err) {
      alert(err instanceof Error ? err.message : `Failed to ${action} user`);
    } finally {
      setActionInProgress(null);
    }
  };

  const handleResetPassword = async (user: AdminUser) => {
    if (!user.email) {
      alert('Cannot reset password: user has no email address');
      return;
    }

    if (!confirm(`Send password reset email to ${user.email}?`)) {
      return;
    }

    setActionInProgress(user.id);
    try {
      const result = await resetPassword.mutateAsync(user.id);
      alert(result.message);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to reset password');
    } finally {
      setActionInProgress(null);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  if (tenantsLoading) {
    return <LoadingCard />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Users</h1>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm font-medium text-gray-500">Total Users</div>
          <div className="mt-1 text-3xl font-semibold text-gray-900">{stats.total}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm font-medium text-gray-500">Enabled</div>
          <div className="mt-1 text-3xl font-semibold text-green-600">{stats.enabled}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm font-medium text-gray-500">Disabled</div>
          <div className="mt-1 text-3xl font-semibold text-red-600">{stats.disabled}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">Filter by Tenant</label>
            <select
              value={selectedTenantId}
              onChange={(e) => setSelectedTenantId(e.target.value)}
              className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All Tenants</option>
              {tenants?.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {usersLoading ? (
          <div className="p-8 text-center text-gray-500">Loading users...</div>
        ) : !users || users.length === 0 ? (
          <div className="p-8 text-center text-gray-500">No users found</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tenant
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Last Login
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {users.map((user) => (
                  <tr key={user.id} className={!user.enabled ? 'bg-gray-50' : ''}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {user.display_name || user.email || 'Unknown'}
                        </div>
                        {user.display_name && user.email && (
                          <div className="text-sm text-gray-500">{user.email}</div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {user.tenant_name || tenantMap[user.tenant_id]?.name || 'Unknown'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          user.enabled
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {user.enabled ? 'Enabled' : 'Disabled'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(user.created_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(user.last_login)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => handleResetPassword(user)}
                          disabled={actionInProgress === user.id || !user.email}
                          title={user.email ? 'Send password reset email' : 'No email address'}
                        >
                          Reset Password
                        </Button>
                        <Button
                          variant={user.enabled ? 'danger' : 'primary'}
                          size="sm"
                          onClick={() => handleToggleEnabled(user)}
                          disabled={actionInProgress === user.id}
                        >
                          {actionInProgress === user.id
                            ? 'Processing...'
                            : user.enabled
                            ? 'Disable'
                            : 'Enable'}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminUsersPage;
